import { Link } from "react-router-dom";
import { Phone, Check, ArrowRight } from "lucide-react";
import { QuoteWidget } from "../components/QuoteWidget";
import { TrustBar } from "../components/TrustBar";
import { SectionHeading } from "../components/SectionHeading";
import { ServicesGrid, CargoTypes, WhyUs } from "../components/Showcase";
import { HowItWorksSteps } from "../components/HowItWorksSteps";
import { Testimonials, useReviews } from "../components/Testimonials";
import { FaqList } from "../components/FaqList";
import { CtaBand } from "../components/CtaBand";
import { useBrand } from "../context/BrandContext";
import { IMAGES } from "../data/content";

const Hero = () => {
  const b = useBrand();
  return (
    <section className="relative overflow-hidden bg-[#F7F8FA] border-b border-slate-200" data-testid="home-hero">
      <div className="absolute inset-0 grid-lines" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-amber-200/40 blur-3xl" />
      <div className="relative z-[2] container-x pt-8 pb-12 md:py-14 lg:py-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-7">
          <span className="eyebrow rise">Caldwell, Idaho · Continental US</span>
          <h1 className="rise d1 mt-4 font-display font-black uppercase text-slate-900 text-[2.6rem] leading-[0.9] sm:text-6xl lg:text-7xl">
            Vehicle & freight<br />transport, <span className="text-brand-600">done right.</span>
          </h1>
          <p className="rise d2 mt-4 text-slate-700 text-sm sm:hidden leading-relaxed">Open & enclosed carriers, door-to-door, all 48 states. No upfront payment.</p>
          <p className="rise d2 mt-5 text-slate-700 text-base md:text-lg max-w-xl leading-relaxed hidden sm:block">
            Open and enclosed car carriers, door-to-door delivery and general freight across all 48 contiguous states. Register online, get a tracking number instantly, pay only after we send your official invoice.
          </p>
          <ul className="rise d3 mt-5 hidden sm:grid grid-cols-2 gap-x-6 gap-y-2 max-w-lg text-sm text-slate-800">
            {["No upfront payment", "Fully insured carriers", "Delivery to your mechanic, no extra cost", "Live milestone tracking"].map((t) => (
              <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-600" />{t}</li>
            ))}
          </ul>
          <div className="rise d4 mt-7 relative hidden lg:block">
            <img src={IMAGES.hero} alt="Car carrier truck on a sunny Idaho highway" className="w-full h-[240px] object-cover object-[60%_55%] border border-slate-200 shadow-[0_18px_40px_-20px_rgba(15,23,42,.35)]" />
            <div className="absolute left-4 bottom-4 flex items-center gap-3">
              <a href={`tel:${b.phone_tel}`} className="flex items-center gap-3 bg-white/95 backdrop-blur px-3 py-2 shadow-md group" data-testid="hero-call-link">
                <span className="w-10 h-10 grid place-items-center bg-amber-500 text-slate-900"><Phone className="w-4 h-4" /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Talk to dispatch</span>
                  <span className="block font-display font-bold text-xl text-slate-900 group-hover:text-brand-600 transition-colors">{b.phone_display}</span>
                </span>
              </a>
              <Link to="/track" className="btn-ghost shadow-md" data-testid="hero-track-link">Track shipment <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 rise d2">
          <QuoteWidget />
        </div>
        <img src={IMAGES.hero} alt="" className="lg:hidden w-full h-48 sm:h-64 object-cover border border-slate-200" />
      </div>
    </section>
  );
};

const Section = ({ children, className = "", testid }) => (
  <section className={`py-20 md:py-28 ${className}`} data-testid={testid}><div className="container-x">{children}</div></section>
);

export default function Home() {
  const reviews = useReviews();
  return (
    <>
      <Hero />
      <TrustBar />
      <Section testid="home-services">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <SectionHeading eyebrow="Services" title="Every way to move a vehicle" sub="From a single sedan to a motorhome or a palletized load, we match the right carrier to the job." />
          <Link to="/services" className="btn-ghost shrink-0" data-testid="home-services-link">All services <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <ServicesGrid />
      </Section>
      <Section className="bg-[#F7F8FA] border-y border-slate-200 grid-lines" testid="home-how-it-works">
        <SectionHeading eyebrow="How it works" title="Four steps. No guesswork." sub="The same process for every shipment, from registration to the moment the keys change hands." className="mb-12" />
        <HowItWorksSteps />
        <Link to="/how-it-works" className="mt-8 inline-flex items-center gap-2 font-display uppercase tracking-wider text-brand-600 hover:text-amber-800" data-testid="home-how-link">Read the full process <ArrowRight className="w-4 h-4" /></Link>
      </Section>
      <Section testid="home-cargo">
        <SectionHeading eyebrow="What we haul" title="Cars, trucks, RVs & freight" className="mb-12" />
        <CargoTypes />
      </Section>
      <Section className="bg-white border-y border-slate-200" testid="home-why-us">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <img src={IMAGES.doorToDoor} alt="Car being delivered to a residential driveway" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-amber-500 text-[#0B1017] p-5 max-w-[220px]">
              <div className="font-display font-black text-4xl leading-none">$0</div>
              <div className="font-mono text-[10px] uppercase tracking-wider mt-2">Extra to deliver to your mechanic</div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <SectionHeading eyebrow="Why Echevarria" title="Built on accountability" sub="A small, focused team in Caldwell, Idaho that answers the phone and tells you the truth about timing." className="mb-12" />
            <WhyUs />
          </div>
        </div>
      </Section>
      {reviews?.length > 0 && (
        <Section testid="home-testimonials">
          <SectionHeading eyebrow="Customers" title="What shippers say" className="mb-12" />
          <Testimonials reviews={reviews} />
        </Section>
      )}
      <Section className="bg-[#F7F8FA] border-t border-slate-200" testid="home-faq">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="FAQ" title="Straight answers" sub="Payment, timing, inspections and more." />
            <Link to="/faq" className="btn-ghost mt-8" data-testid="home-faq-link">All questions <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="lg:col-span-8"><FaqList limit={5} /></div>
        </div>
      </Section>
      <CtaBand />
    </>
  );
}
